import React, { useEffect, useState } from 'react';
import API from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import Icon from '../components/Icon';
import { FormField, Input, Select, SubmitBtn } from '../components/FormField';

const TYPES = ['exam','quiz','homework','project','midterm','final','quarter'];

export default function Grades() {
  const { t, lang } = useLanguage();
  const TNAMES = {
    exam: t('grades.typeExam'),
    quiz: t('grades.typeQuiz'),
    homework: t('grades.typeHomework'),
    project: t('grades.typeProject'),
    midterm: t('grades.typeMidterm'),
    final: t('grades.typeFinal'),
    quarter: t('grades.typeQuarter'),
  };
  const locale = lang === 'en' ? 'en-US' : 'az-AZ';

  const [grades, setGrades] = useState([]);
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({student_id:'',subject_id:'',class_id:'',grade:'',max_grade:100,grade_type:'exam',quarter:'',date:new Date().toISOString().split('T')[0],notes:''});
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState({class_id:'',subject_id:'',quarter:''});

  const load = async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (filter.class_id) p.set('class_id',filter.class_id);
    if (filter.subject_id) p.set('subject_id',filter.subject_id);
    if (filter.quarter) p.set('quarter',filter.quarter);
    const [g,s,sub,c] = await Promise.all([API.get('/grades?'+p), API.get('/students'), API.get('/subjects'), API.get('/classes')]);
    setGrades(g.data); setStudents(s.data); setSubjects(sub.data); setClasses(c.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, [filter]);

  const f = (k, v) => setForm(p => ({...p, [k]: v}));
  const handleSave = async e => {
    e.preventDefault(); setSaving(true);
    try {
      if (modal === 'add') await API.post('/grades', form);
      else await API.put('/grades/'+form.id, form);
      setModal(null); load();
    } finally { setSaving(false); }
  };

  const tSubj = (n) => lang === 'en' ? t('subjectNames.' + n, n) : n;
  const columns = [
    { header:'#', render:(r,i)=>i+1, width:'40px' },
    { header:t('grades.colStudent'), render: r => r.student_name },
    { header:t('grades.colSubject'), render: r => tSubj(r.subject_name) },
    { header:t('grades.colType'), render: r => TNAMES[r.grade_type]||r.grade_type },
    { header:t('grades.colQuarter'), render: r => r.quarter ? r.quarter+t('grades.quarterSuffix') : '—' },
    { header:t('grades.colGrade'), render: r => (
      <span style={{fontWeight:700,color:r.grade>=51?'#27ae60':'#e74c3c'}}>{r.grade}/{r.max_grade}</span>
    )},
    { header:t('grades.colDate'), render: r => r.date ? new Date(r.date).toLocaleDateString(locale) : '—' },
    { header:t('common.actions'), render: r => (
      <div style={{display:'flex',gap:6}}>
        <button className="btn btn-sm btn-secondary" onClick={()=>{setForm({...r,date:r.date?r.date.split('T')[0]:''}); setModal('edit');}}><Icon name="edit" size={15} /></button>
        <button className="btn btn-sm btn-danger" onClick={async()=>{if(window.confirm(t('common.deleteConfirm'))){await API.delete('/grades/'+r.id); load();}}}><Icon name="trash" size={15} /></button>
      </div>
    )}
  ];

  return (
    <div>
      <PageHeader icon="grades" title={t('grades.title')} subtitle={`${t('common.total')} ${grades.length} ${t('grades.countSuffix')}`}
        action={<button className="btn btn-primary" onClick={()=>{setForm({student_id:'',subject_id:'',class_id:'',grade:'',max_grade:100,grade_type:'exam',quarter:'',date:new Date().toISOString().split('T')[0],notes:''}); setModal('add');}}>{t('grades.btnAdd')}</button>} />
      <div style={{display:'flex',gap:12,marginBottom:16,flexWrap:'wrap'}}>
        <select className="form-input" style={{width:130}} value={filter.class_id} onChange={e=>setFilter(p=>({...p,class_id:e.target.value}))}>
          <option value="">{t('grades.allClasses')}</option>
          {classes.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="form-input" style={{width:160}} value={filter.subject_id} onChange={e=>setFilter(p=>({...p,subject_id:e.target.value}))}>
          <option value="">{t('grades.allSubjects')}</option>
          {subjects.map(s=><option key={s.id} value={s.id}>{tSubj(s.name)}</option>)}
        </select>
        <select className="form-input" style={{width:110}} value={filter.quarter} onChange={e=>setFilter(p=>({...p,quarter:e.target.value}))}>
          <option value="">{t('grades.allQuarters')}</option>
          {[1,2,3,4].map(q=><option key={q} value={q}>{q}{t('grades.quarterSuffix')}</option>)}
        </select>
      </div>
      <DataTable columns={columns} data={grades} loading={loading} />
      {modal && (
        <Modal title={modal==='add'?t('grades.modalNew'):t('grades.modalEdit')} onClose={()=>setModal(null)} size="lg">
          <form onSubmit={handleSave}>
            <div className="form-row">
              <FormField label={t('grades.fieldStudent')} required>
                <Select value={form.student_id} onChange={e=>f('student_id',e.target.value)} required>
                  <option value="">{t('common.select')}</option>
                  {students.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                </Select>
              </FormField>
              <FormField label={t('grades.fieldSubject')} required>
                <Select value={form.subject_id} onChange={e=>f('subject_id',e.target.value)} required>
                  <option value="">{t('common.select')}</option>
                  {subjects.map(s=><option key={s.id} value={s.id}>{tSubj(s.name)}</option>)}
                </Select>
              </FormField>
            </div>
            <div className="form-row">
              <FormField label={t('grades.fieldType')}>
                <Select value={form.grade_type} onChange={e=>f('grade_type',e.target.value)}>
                  {TYPES.map(ty=><option key={ty} value={ty}>{TNAMES[ty]}</option>)}
                </Select>
              </FormField>
              <FormField label={t('grades.fieldQuarter')}>
                <Select value={form.quarter} onChange={e=>f('quarter',e.target.value)}>
                  <option value="">—</option>
                  {[1,2,3,4].map(q=><option key={q} value={q}>{q}{t('grades.quarterSuffix')}</option>)}
                </Select>
              </FormField>
            </div>
            <div className="form-row">
              <FormField label={t('grades.fieldGrade')} required><Input type="number" min="0" max="100" value={form.grade} onChange={e=>f('grade',e.target.value)} required /></FormField>
              <FormField label={t('grades.fieldDate')}><Input type="date" value={form.date} onChange={e=>f('date',e.target.value)} /></FormField>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={()=>setModal(null)}>{t('common.cancel')}</button>
              <SubmitBtn loading={saving}>{modal==='add'?t('common.add'):t('common.save')}</SubmitBtn>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
